import React, { useState, useCallback } from 'react';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import type { TuPreferesCard } from '../data/soft';

interface TuPreferesRevealProps {
  card: TuPreferesCard;
  playerName: string;
  players: string[];
  onDone: () => void;
}

function substituteName(text: string, currentPlayer: string, allPlayers: string[]): string {
  const others = allPlayers.filter(p => p !== currentPlayer);
  const prenom2 = others.length > 0 ? others[Math.floor(Math.random() * others.length)] : currentPlayer;
  return text.replace(/\[Prénom2\]/g, prenom2).replace(/\[Prénom\]/g, currentPlayer);
}

function dynamicFontSize(text: string): string {
  if (text.length > 120) return '14px';
  if (text.length > 80) return '16px';
  if (text.length > 50) return '17px';
  return '18px';
}

export default function TuPreferesReveal({ card, playerName, players, onDone }: TuPreferesRevealProps) {
  const [processedCard] = useState(() => ({
    optionA: substituteName(card.optionA, playerName, players),
    optionB: substituteName(card.optionB, playerName, players),
  }));
  const [chosen, setChosen] = useState<'A' | 'B' | null>(null);
  const [drinkSide] = useState<'A' | 'B'>(() => Math.random() > 0.5 ? 'A' : 'B');

  const handleChoose = useCallback((side: 'A' | 'B') => {
    if (chosen) return;
    setChosen(side);
    try { Haptics.impact({ style: ImpactStyle.Medium }); } catch {}

    setTimeout(() => {
      try {
        if (side === drinkSide) {
          Haptics.impact({ style: ImpactStyle.Heavy });
        } else {
          Haptics.notification({ type: NotificationType.Success });
        }
      } catch {}
    }, 400);
  }, [chosen, drinkSide]);

  const aDrinks = drinkSide === 'A';
  const bDrinks = drinkSide === 'B';

  const cardStyle = (side: 'A' | 'B') => {
    if (!chosen) return 'bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 border-blue-400/20 shadow-[0_6px_24px_rgba(59,130,246,0.25)]';
    const drinks = side === 'A' ? aDrinks : bDrinks;
    if (drinks) return 'bg-gradient-to-br from-red-500 via-orange-500 to-red-600 border-red-400/30 shadow-[0_6px_24px_rgba(239,68,68,0.4)]';
    return 'bg-gradient-to-br from-green-400 via-emerald-500 to-green-600 border-green-300/30 shadow-[0_6px_24px_rgba(74,222,128,0.4)]';
  };

  const isChosen = (side: 'A' | 'B') => chosen === side;

  return (
    <div className="h-screen bg-gradient-to-b from-blue-800 via-blue-900 to-[#0a1628] flex flex-col safe-top safe-bottom">
      {/* Header */}
      <div className="flex-shrink-0 pt-14 pb-1 px-6 text-center">
        <p className="text-blue-300/50 font-body text-xs mb-1">C'est au tour de</p>
        <h1 className="font-display font-black text-3xl text-white drop-shadow-lg mb-2">
          {playerName}
        </h1>
        <p className={`text-blue-300/60 font-body text-sm ${!chosen ? 'tp-pulse' : ''}`}>
          {!chosen ? 'Tu préfères...' : 'Verdict !'}
        </p>
      </div>

      {/* Two option cards — always face visible */}
      <div className="flex-1 flex flex-col items-center justify-center px-5 gap-0 min-h-0">
        {/* Option A */}
        <button
          className={`w-full max-w-sm rounded-2xl border px-5 py-4 text-center transition-all duration-300 btn-press ${cardStyle('A')} ${
            chosen && !isChosen('A') ? 'opacity-60 scale-95' : ''
          } ${isChosen('A') ? 'scale-105 ring-2 ring-white/30' : ''}`}
          style={{ minHeight: '100px' }}
          onClick={() => handleChoose('A')}
          disabled={!!chosen}
        >
          <p className="text-white font-display font-bold text-center leading-snug" style={{ fontSize: dynamicFontSize(processedCard.optionA) }}>
            {processedCard.optionA}
          </p>
          {chosen && (
            <p className="mt-2 font-display font-bold text-sm">
              {aDrinks ? '🍺 Bois 2 gorgées !' : '🎉 T\'as de la chance !'}
            </p>
          )}
        </button>

        {/* OU separator */}
        <div className="flex-shrink-0 py-3">
          <span className="font-display font-black text-xl text-white/30">OU</span>
        </div>

        {/* Option B */}
        <button
          className={`w-full max-w-sm rounded-2xl border px-5 py-4 text-center transition-all duration-300 btn-press ${cardStyle('B')} ${
            chosen && !isChosen('B') ? 'opacity-60 scale-95' : ''
          } ${isChosen('B') ? 'scale-105 ring-2 ring-white/30' : ''}`}
          style={{ minHeight: '100px' }}
          onClick={() => handleChoose('B')}
          disabled={!!chosen}
        >
          <p className="text-white font-display font-bold text-center leading-snug" style={{ fontSize: dynamicFontSize(processedCard.optionB) }}>
            {processedCard.optionB}
          </p>
          {chosen && (
            <p className="mt-2 font-display font-bold text-sm">
              {bDrinks ? '🍺 Bois 2 gorgées !' : '🎉 T\'as de la chance !'}
            </p>
          )}
        </button>
      </div>

      {/* Tour suivant */}
      <div className="flex-shrink-0 px-6 pb-6" style={{ minHeight: '72px' }}>
        {chosen && (
          <button
            onClick={onDone}
            className="w-full py-4 rounded-full bg-gradient-to-r from-green-400 to-emerald-500 text-white font-display font-bold text-base btn-press animate-fade-in shadow-[0_4px_20px_rgba(74,222,128,0.3)]"
          >
            Tour suivant →
          </button>
        )}
      </div>
    </div>
  );
}
